import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/button'
import { StoreAndroidAppUrl, StoreIOSAppUrl } from 'const'
import { logEvent } from 'firebase/analytics'
import { analytics } from 'lib/firebase'

function AppleLogo({ className }: { className?: string }) {
	return (
		<svg className={className} viewBox='0 0 24 24' fill='currentColor'>
			<path d='M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z' />
		</svg>
	)
}

function GooglePlayLogo({ className }: { className?: string }) {
	return (
		<svg className={className} viewBox='0 0 24 24' fill='currentColor'>
			<path d='M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z' />
		</svg>
	)
}

const screens = [
	{
		light: 'https://minio.entrum.ru/web/screens/ios/light/screen_maps.jpeg',
		dark: 'https://minio.entrum.ru/web/screens/ios/dark/screen_maps.jpeg',
		label: 'Map search',
	},
	{
		light: 'https://minio.entrum.ru/web/screens/ios/light/screen_restaurant.jpeg',
		dark: 'https://minio.entrum.ru/web/screens/ios/dark/screen_restaurant.jpeg',
		label: 'Venue card',
	},
	{
		light: 'https://minio.entrum.ru/web/screens/ios/light/screen_reviews.jpeg',
		dark: 'https://minio.entrum.ru/web/screens/ios/dark/screen_reviews.jpeg',
		label: 'Reviews',
	},
	{
		light: 'https://minio.entrum.ru/web/screens/ios/light/screen_feed.jpeg',
		dark: 'https://minio.entrum.ru/web/screens/ios/dark/screen_feed.jpeg',
		label: 'Dish feed',
	},
]

export default function HeroSection() {
	const { t } = useTranslation()

	function openAppStore() {
		logEvent(analytics, 'store_click', { store: 'app_store', location: 'hero' })
		window.location.href = StoreIOSAppUrl
	}

	function openGooglePlay() {
		logEvent(analytics, 'store_click', { store: 'google_play', location: 'hero' })
		window.location.href = StoreAndroidAppUrl
	}

	return (
		<div className='flex flex-col md:flex-1 md:overflow-hidden'>
			{/* App identity */}
			<div className='pt-8 pb-5 px-6 flex flex-col items-center text-center gap-3'>
				<img
					src='/image/icon_rounded.png'
					alt='Breadcrumbs'
					className='w-20 h-20 rounded-[22px] shadow-lg'
				/>
				<div>
					<h1 className='text-5xl font-bold tracking-tight'>{t('common.appName')}</h1>
					<p className='text-sm text-muted-foreground mt-1'>{t('hero.description')}</p>
				</div>
				<div className='flex gap-3'>
					<Button className='rounded-full px-6 font-semibold gap-2 h-10' onClick={openAppStore}>
						<AppleLogo className='w-4 h-4' />
						{t('hero.appStore')}
					</Button>
					<Button variant='outline' className='rounded-full px-6 gap-2 h-10' onClick={openGooglePlay}>
						<GooglePlayLogo className='w-4 h-4' />
						{t('hero.googlePlay')}
					</Button>
				</div>
				<p className='text-xs text-muted-foreground'>{t('hero.free')}</p>
			</div>

			{/* Screenshots carousel
			    mobile: fixed phone height, snap-scroll per screen
			    desktop: flex-1, phones fill remaining height */}
			<div className='overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory md:flex-1'>
				<div className='flex gap-3 px-6 py-4 md:h-full md:items-center md:justify-center'>
					{screens.map((s, i) => (
						<div
							key={i}
							className='relative aspect-[9/19.5] h-[420px] md:h-full shrink-0 snap-center bg-black border-[4px] border-black rounded-[2rem] shadow-xl overflow-hidden ring-1 ring-zinc-700/30'>
							<div className='absolute top-1 inset-x-0 mx-auto w-[44px] h-[12px] bg-black rounded-full z-20' />
							<img src={s.light} alt={s.label} className='absolute inset-0 w-full h-full object-fill z-10 dark:hidden' />
							<img src={s.dark} alt={s.label} className='absolute inset-0 w-full h-full object-fill z-10 hidden dark:block' />
						</div>
					))}
				</div>
			</div>
		</div>
	)
}
